/* eslint-disable func-names */
import { useCallback, useMemo, useRef } from 'react'
import { useNavigate } from '@reach/router'
import AlertTitle from '@mui/material/AlertTitle'
import { format } from 'date-fns'
import { QueryObserverResult } from 'react-query'
import { useTranslation } from 'react-i18next'
import Breadcrumb from 'src/components/shared/Breadcrumb/Breadcrumb'
import AsyncEnhancedTable from 'src/components/shared/EnhancedTable/AsyncEnhancedTable'
import Alert from 'src/components/shared/ui/Alert'
import { InfoLxFillIcon } from 'src/components/shared/ui/Icons'
import MainContentPaper from 'src/components/shared/ui/MainContentPaper'
import ToolTipEllipsis from 'src/components/shared/ui/TooltipEllipsis'
import { theme } from 'src/providers/ThemeProvider'
import { MESSAGE } from 'src/constants/routes'
import { TipoMensaje, TipoMensajeDescripcion } from 'src/enums/EscritosLegales'
import { EstadoMensaje, EstadoLexnetMensaje } from 'src/enums/EstadoMensaje'
import { Mensaje } from 'src/interfaces'
import { GetMessagesResponse } from 'src/services/MessageService'
import SujetoCodigo from './components/Sujeto/Codigo'
import useStyles from './hooks/useStyles'

const alertStyles = {
  alert: {
    padding: theme.spacing(1, 1.5),

    '& .MuiAlert-message': {
      padding: '2px 0 0 0'
    },
    marginBottom: '12px'
  },

  title: {
    fontFamily: theme.corporate.primaryFont,
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '20px',
    marginBottom: '5px'
  }
}

type Props = {
  onChangePaginationOptions: ({
    pageIndex,
    pageSize
  }: {
    pageIndex: string
    pageSize: string
  }) => void
  messagesQuery: QueryObserverResult<GetMessagesResponse>
  tipoMensaje: TipoMensaje[]
  subsection: string
  pageSize: number
  showInfoAlert?: boolean
}

export default function MessagesView({
  onChangePaginationOptions,
  messagesQuery,
  tipoMensaje,
  subsection,
  pageSize,
  showInfoAlert
}: Props): JSX.Element {
  const navigate = useNavigate()
  const classes = useStyles()
  const { t } = useTranslation()
  const pageCount = useRef<number | null>(null)

  const columns = useMemo(
    () => [
      // {
      //   id: 'leido',
      //   Header: '',
      //   accessor: () => '',
      //   Cell: ({ row }) => {
      //     if (row.original.fechaUltimaDescarga !== null) {
      //       return <EmailOpenIcon />
      //     }
      //     return <EmailCloseIcon />
      //   },
      //   width: 30
      // },
      {
        id: 'id',
        Header: 'Identificador LexNET',
        accessor: 'id',
        width: 150
      },
      {
        id: 'tipoMensajeMaster.descripcion',
        Header: 'Tipo Escrito',
        accessor: (mensaje: Mensaje) => {
          if (mensaje.escritos?.tipoEscrito.descripcion) {
            return TipoMensajeDescripcion[
              mensaje.escritos.tipoEscrito.descripcion.replace(' ', '_')
            ]
          }
          return null
        },
        width: 100
      },
      {
        id: 'origen.nombre',
        Header: 'Remitente',
        accessor: (mensaje: Mensaje) => mensaje.mensajeOrigenes[0]?.origen,
        Cell: ({ row }) => {
          return (
            <SujetoCodigo
              sujeto={row.original.mensajeOrigenes[0]?.origen}
              variant="body2"
              className={classes.cell}
            />
          )
        },
        width: 75
      },
      {
        id: 'datos.asunto',
        Header: 'Asunto',
        accessor: (mensaje: Mensaje) => mensaje.datos.asunto,
        width: 125
      },
      {
        id: 'datos.numeroProcedimiento',
        Header: 'Número Procedimiento',
        accessor: (mensaje: Mensaje) => mensaje.datos.numeroProcedimiento,
        width: 100
      },
      {
        id: 'destino.nombre',
        Header: 'Destinatario',
        accessor: (mensaje: Mensaje) => mensaje.mensajeDestinos[0]?.destino,
        Cell: ({ row }) => {
          return (
            <SujetoCodigo
              sujeto={row.original.mensajeDestinos[0]?.destino}
              variant="body2"
              className={classes.cell}
            />
          )
        },
        width: 100
      },
      {
        id: 'datos.fechaEnvio',
        Header: 'Fecha de envío',
        accessor: (mensaje: Mensaje) => format(new Date(mensaje.datos.fechaEnvio), 'dd/MM/yyyy HH:mm'),
        width: 75
      },
      {
        id: 'fechaUltimaDescarga',
        Header: 'Fecha de aceptación',
        accessor: (mensaje: Mensaje) =>
          mensaje.leido || mensaje.fechaUltimaDescarga === null
            ? '--/--/----'
            : format(new Date(mensaje.fechaUltimaDescarga), 'dd/MM/yyyy HH:mm'),
        width: 75
      },
      {
        id: tipoMensaje[0] === TipoMensaje.NOTIFICACION ? 'estado' : 'estadoLexnet',
        Header: 'Estado',
        accessor: (mensaje: Mensaje) => {
          // Va antes que "leido": tras consolidar el acuse LexNET marca el mensaje como
          // leído, pero lo que importa al usuario es que aún falta descargarlo.
          if (mensaje.estado === EstadoMensaje.PENDIENTE_DESCARGA) {
            return 'Pendiente de descarga'
          }
          if (mensaje.leido) {
            return 'Firmada en origen'
          }
          let title = mensaje.estado === EstadoMensaje.INITIAL ? 'Pendiente de firma' : 'Firmada en LexON'
          if (mensaje.estado === EstadoMensaje.FIRMADO_EN_ORIGEN) {
            title = 'Firmada en origen'
          }

          if (mensaje.tipoMensaje === TipoMensaje.ACUSE.toString()) {
            if (mensaje.estadoLexnet <= EstadoLexnetMensaje.PENDIENTE) {
              title = 'Pendiente'
            } else if (mensaje.estadoLexnet === EstadoLexnetMensaje.ACEPTADO) {
              title = 'Aceptado'
            } else if (mensaje.estadoLexnet === EstadoLexnetMensaje.RECHAZADO) {
              title = 'Rechazado'
            } else if (mensaje.estadoLexnet === EstadoLexnetMensaje.SUBSANADO) {
              title = 'Subsanado'
            }
          }
          return title
        },
        width: 110,
        Cell: ({ value, row }) => {
          const mensaje = row.original
          let className =
            mensaje.estado === EstadoMensaje.INITIAL || mensaje.estado === EstadoMensaje.PENDIENTE_DESCARGA
              ? classes.initial
              : classes.signed
          if (
            mensaje.estado !== EstadoMensaje.PENDIENTE_DESCARGA &&
            (mensaje.leido || mensaje.estado === EstadoMensaje.FIRMADO_EN_ORIGEN)
          ) {
            className = classes.firmadoEnOrigen
          }

          if (mensaje.tipoMensaje === TipoMensaje.ACUSE.toString()) {
            if (mensaje.estadoLexnet <= EstadoLexnetMensaje.PENDIENTE) {
              className = classes.initial
            } else if (mensaje.estadoLexnet === EstadoLexnetMensaje.ACEPTADO) {
              className = classes.signed
            } else if (mensaje.estadoLexnet === EstadoLexnetMensaje.RECHAZADO) {
              className = classes.rejected
            } else if (mensaje.estadoLexnet === EstadoLexnetMensaje.SUBSANADO) {
              className = classes.signed
            }
          }
          return (
            <ToolTipEllipsis text={value}>
              <span className={`${classes.state} ${className}`}>{value}</span>
            </ToolTipEllipsis>
          )
        }
      },
      {
        Header: 'Expediente',
        accessor: (mensaje: Mensaje) =>
          mensaje.estado === EstadoMensaje.ASIGNADO_EXPEDIENTE
            ? mensaje.lexonClassifications.map(
                lexonClassification =>
                  `${lexonClassification.expedientIdOwnFile ??
                    ''} - ${lexonClassification.expedientDescription ?? ''}`
              )
            : '',
        width: 100,
        Cell: ({ row }) =>
          row.original.estado === EstadoMensaje.ASIGNADO_EXPEDIENTE ? (
            <div className={classes.cellExpedient}>
              {row.original.lexonClassifications.map(lexonClassification => {
                const text =
                  lexonClassification.expedientDescription &&
                  lexonClassification?.expedientDescription?.includes(
                    lexonClassification?.expedientIdOwnFile
                  )
                    ? lexonClassification.expedientDescription
                    : `${lexonClassification.expedientIdOwnFile ??
                        ''} - ${lexonClassification.expedientDescription ?? ''}`

                return (
                  <ToolTipEllipsis key={lexonClassification.id} text={text}>
                    <div>{text}</div>
                  </ToolTipEllipsis>
                )
              })}
            </div>
          ) : (
            ''
          ),
        disableSortBy: true
      }
    ],
    // eslint-disable-next-line
    [classes.initial, classes.signed]
  )

  if (tipoMensaje[0] === TipoMensaje.NOTIFICACION) {
    const index = columns.findIndex(column => column.id === 'tipoMensajeMaster.descripcion')
    columns.splice(index, 1)
  } else {
    const index = columns.findIndex(column => column.id === 'fechaUltimaDescarga')
    if (index !== -1) {
      columns.splice(index, 1)
    }
  }
  const handleClickedRow = useCallback(
    function(row) {
      navigate(MESSAGE(row.original.id))
    },
    [navigate]
  )

  if (messagesQuery.isSuccess) {
    pageCount.current = Math.ceil(messagesQuery.data.total / pageSize)
  }

  return (
    <section>
      <Breadcrumb section={subsection} />
      <MainContentPaper>
        {showInfoAlert && (
          <Alert
            severity="info"
            sx={alertStyles.alert}
            icon={<InfoLxFillIcon htmlColor={theme.colors.blue01} />}
          >
            <AlertTitle sx={alertStyles.title}>
              {t('components.avisos_lexnet.info_alert.title')}
            </AlertTitle>
            <div>{t('components.avisos_lexnet.info_alert.message_1')}</div>
            <div>{t('components.avisos_lexnet.info_alert.message_2')}</div>
          </Alert>
        )}
        <AsyncEnhancedTable
          columns={columns}
          data={messagesQuery.data?.results ?? []}
          onChangePaginationOptions={onChangePaginationOptions}
          pageCount={pageCount.current ?? 0}
          pageSize={pageSize}
          totalRowsCount={messagesQuery.data?.total ?? 0}
          isFetching={messagesQuery.isLoading}
          isSuccess={messagesQuery.isSuccess}
          isError={messagesQuery.isError}
          onClickRow={handleClickedRow}
        />
      </MainContentPaper>
    </section>
  )
}
